import { SmartCaptcha } from '@yandex/smart-captcha';
import axios from 'axios';
import React, { Dispatch, FormEvent, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '../../index.styles';
import { Footer } from '../Footer/Footer';
import { ButtonsContainer } from '../Header/Header.styles';
import { CentredDiv, ErrorText, Form, Input, RegistrationButton, StyledText, StyledTitle, LinkButton } from './Registration.styles';

interface IRegistrationRegRequest {
  fio: string;
  'smart-token': string;
  email?: string;
  phone?: string;
}

interface IRegistrationResendRequest {
  'smart-token': string;
  email?: string;
  phone?: string;
}

interface IRegistrationConfirmRequest extends IRegistrationResendRequest {
  code: string;
  type?: ERegistrationType;
}

class ApiRegistration {
  private _apiUrl = '';

  public async registration(requestData: IRegistrationRegRequest) {
    const response = await axios.post(`${this._apiUrl}/api/register`, requestData);
    return response.data;
  }

  public async resend(requestData: IRegistrationResendRequest) {
    const response = await axios.post(`${this._apiUrl}/api/resend-verification`, requestData);
    return response.data;
  }

  public async confirm(requestData: IRegistrationConfirmRequest) {
    const { type, ...rest } = requestData;
    const urlConfirm = type === ERegistrationType.phone ? 'verify-phone' : 'verify-email';

    const response = await axios.post(`${this._apiUrl}/api/${urlConfirm}`, rest);
    return response.data;
  }
}

enum ERegistrationStatus {
  registrate,
  confirm,
  sucess,
}

enum ERegistrationType {
  phone,
  email,
}

interface RegistrationFormProps {
  readonly login: string;
  readonly setLogin: Dispatch<SetStateAction<string>>;
  readonly changeStatus: Dispatch<SetStateAction<ERegistrationStatus>>;
  readonly changeType: Dispatch<SetStateAction<ERegistrationType>>;
}

const apiServise = new ApiRegistration();
const SITE_KEY = 'ysc1_cuvC6bdM5nHmmmbECA2I6j78heSeXOMbgGOHbgaN4aa10903';

const RegistrationForm = (props: RegistrationFormProps) => {
  const { login, setLogin, changeStatus, changeType } = props;
  const { t } = useTranslation();
  const [fio, setFio] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [token, setToken] = useState('');
  const [resetCaptcha, setResetCaptcha] = useState(0);

  const handleReset = useCallback(() => {
    setToken('');
    setResetCaptcha((prev) => prev + 1);
  }, [setToken, setResetCaptcha]);

  const onRegistration = useCallback(async (e: FormEvent<any>) => {
    e.preventDefault();
    if (!token) {
      setErrors(['Не пройдена валидация']);
      return;
    }

    const isEmail = login.includes('@');
    changeType(isEmail ? ERegistrationType.email : ERegistrationType.phone);

    const requestData: IRegistrationRegRequest = {
      fio,
      'smart-token': token,
    };

    if (isEmail) {
      requestData['email'] = login;
    } else {
      requestData['phone'] = login;
    }

    try {
      await apiServise.registration(requestData);
      changeStatus(ERegistrationStatus.confirm);
    } catch (e: any) {
      if (e?.response?.data?.data) {
        const errors = { ...e?.response?.data?.data ?? {} };
        // @ts-ignore
        setErrors(Object.values(errors).flat(1));
      } else {
        setErrors([e.message]);
      }
    } finally {
      handleReset();
    }
  }, [fio, login, setErrors, token, handleReset]);

  const onResend = useCallback(async (e: FormEvent<any>) => {
    e.preventDefault();
    if (!token) {
      setErrors(['Не пройдена валидация']);
      return;
    }

    const isEmail = login.includes('@');
    changeType(isEmail ? ERegistrationType.email : ERegistrationType.phone);

    const requestData: IRegistrationResendRequest = {
      'smart-token': token,
    };

    if (isEmail) {
      requestData['email'] = login;
    } else {
      requestData['phone'] = login;
    }

    try {
      await apiServise.resend(requestData);
      changeStatus(ERegistrationStatus.confirm);
    } catch (e: any) {
      if (e?.response?.data?.data) {
        const errors = { ...e?.response?.data?.data ?? {} };
        setErrors(Object.values(errors));
      } else {
        setErrors([e.message]);
      }
    } finally {
      handleReset();
    }
  }, [fio, login, setErrors, token, handleReset]);

  const alreadyExist = errors.includes('Этот email уже используется') || errors.includes('Этот телефон уже используется');
  if (alreadyExist) {
    return (
      <CentredDiv>
        <Form onSubmit={onResend}>
          {errors.map(error => {
            return (
              <ErrorText>{error}</ErrorText>
            );
          })}
          <Input
            required
            name={'login'}
            placeholder={t('REGISTRATION.FORM.LOGIN')}
            value={login}
            onChange={e => setLogin(e.target.value)} />
          <SmartCaptcha
            key={`captcha_request_${resetCaptcha}`}
            sitekey={SITE_KEY}
            onSuccess={setToken}
            onTokenExpired={handleReset}
          />
          <RegistrationButton type={'submit'}>
            {t('REGISTRATION.ACTIONS.RESEND')}
          </RegistrationButton>
        </Form>
      </CentredDiv>
    );
  }

  return (
    <CentredDiv>
      <Form onSubmit={onRegistration}>
        {errors.map(error => {
          return (
            <ErrorText>{error}</ErrorText>
          );
        })}
        <Input
          required
          name={'fio'}
          placeholder={t('REGISTRATION.FORM.FIO')}
          value={fio}
          onChange={e => setFio(e.target.value)}
        />
        <Input
          required
          name={'login'}
          placeholder={t('REGISTRATION.FORM.LOGIN')}
          value={login}
          onChange={e => setLogin(e.target.value)} />

        <div style={{display: 'flex', flexDirection: 'row', paddingBottom: 16}}>
          <div style={{ paddingRight: 16, }}>
            <input id={'checkbox'} name={'checkbox'} type={'checkbox'} required />
          </div>
          {/* @ts-ignore */}
          <label for="checkbox">
              Принимаю условия
              <a rel={'noreferrer'} target={'_blank'} href={'/oferta.docx'}> публичной оферты </a>
              и даю согласие на обработку персональных данных согласно правилам, изложенным в данной оферте
            </label>
        </div>

        <SmartCaptcha
          key={`captcha_${resetCaptcha}`}
          sitekey={SITE_KEY}
          onSuccess={setToken}
          onTokenExpired={handleReset}
        />
        <RegistrationButton type={'submit'}>
          {t('REGISTRATION.ACTIONS.REGISTRATE')}
        </RegistrationButton>
      </Form>
    </CentredDiv>
  );
};

const ConfirmForm = (props: { login: string; type: ERegistrationType, changeStatus: Dispatch<SetStateAction<ERegistrationStatus>> }) => {
  const { type, login, changeStatus } = props;
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [token, setToken] = useState('');
  const [resetCaptcha, setResetCaptcha] = useState(0);
  const [disabled, setDisables] = useState(true);

  const startReset = useCallback(() => {
    setTimeout(() => setDisables(false), 60 * 1000);
  }, [setDisables]);

  useEffect(() => {
    startReset()
  }, []);

  const handleReset = useCallback(() => {
    setToken('');
    setResetCaptcha((prev) => prev + 1);
  }, [setToken, setResetCaptcha]);

  const onRegistration = useCallback(async (e: FormEvent<any>) => {
    e.preventDefault();

    const requestData: IRegistrationConfirmRequest = {
      'smart-token': token,
      type,
      code,
    };

    if (type === ERegistrationType.phone) {
      requestData['phone'] = login;
    } else {
      requestData['email'] = login;
    }

    try {
      await apiServise.confirm(requestData);
      changeStatus(ERegistrationStatus.sucess);
    } catch (e: any) {
      if (e?.response?.data?.data) {
        const errors = { ...e?.response?.data?.data ?? {} };
        setErrors(Object.values(errors));
      } else {
        setErrors([e.message]);
      }
    } finally {
      handleReset();
    }
  }, [type, code, changeStatus, token, handleReset]);

  const onResend = useCallback(async (e: FormEvent<any>) => {
    e.preventDefault();
    if (!token) {
      setErrors(['Не пройдена валидация']);
      return;
    }

    const requestData: IRegistrationResendRequest = {
      'smart-token': token,
    };

    if (type === ERegistrationType.email) {
      requestData['email'] = login;
    } else {
      requestData['phone'] = login;
    }

    try {
      await apiServise.resend(requestData);
      changeStatus(ERegistrationStatus.confirm);
    } catch (e: any) {
      if (e?.response?.data?.data) {
        const errors = { ...e?.response?.data?.data ?? {} };
        setErrors(Object.values(errors));
      } else {
        setErrors([e.message]);
      }
    } finally {
      startReset();
      handleReset();
    }
  }, [login, setErrors, token, startReset, handleReset]);

  const placeholder = type === ERegistrationType.email
    ? t('REGISTRATION.FORM.CODE_EMAIL')
    : t('REGISTRATION.FORM.CODE_PHONE');

  return (
    <CentredDiv>
      <Form onSubmit={onRegistration}>
        {errors.map(error => {
          return (
            <ErrorText>{error}</ErrorText>
          );
        })}
        <Input
          name={'code'} placeholder={placeholder}
          value={code}
          onChange={e => setCode(e.target.value)}
        />
        <SmartCaptcha
          key={`captcha_${resetCaptcha}`}
          sitekey={SITE_KEY}
          onSuccess={setToken}
          onTokenExpired={handleReset}
        />
        <RegistrationButton type={'submit'}>
          {t('REGISTRATION.ACTIONS.CONFIRM')}
        </RegistrationButton>
        <RegistrationButton onClick={onResend} disabled={disabled}>
          {t('REGISTRATION.ACTIONS.RESEND')}
        </RegistrationButton>
      </Form>
    </CentredDiv>
  );

};
const Success = () => {
  const { t } = useTranslation();

  return (
    <ButtonsContainer>
      <StyledText>
        {t('REGISTRATION.SUCCESS')}
      </StyledText>
    </ButtonsContainer>
  );
};

const Registration = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(ERegistrationStatus.registrate);
  const [regType, setRegType] = useState(ERegistrationType.email);
  const [login, setLogin] = useState('');

  return (
    <div>
      <Container>
        <StyledTitle>
          {t('REGISTRATION.TITLE')}
        </StyledTitle>
        {status === ERegistrationStatus.registrate && (
          <RegistrationForm
            login={login}
            setLogin={setLogin}
            changeStatus={setStatus}
            changeType={setRegType}
          />
        )}
        {status === ERegistrationStatus.confirm && (
          <ConfirmForm login={login} type={regType} changeStatus={setStatus} />
        )}
        {status === ERegistrationStatus.sucess && (
          <Success />
        )}
        <CentredDiv style={{marginTop: 20}}>
          <LinkButton to={'/'}>
            {t('REGISTRATION.GO_HOME')}
          </LinkButton>
        </CentredDiv>
      </Container>
      <Footer />
    </div>
  );
};

export default Registration;
